import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, NavLink, useNavigate } from "react-router-dom";
import axios from "axios";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider, useTema } from "./context/ThemeContext";
import { useSessionTimeout } from "./hooks/useSessionTimeout";
import SessionTimeoutModal from "./components/SessionTimeoutModal";
import { NotificationProvider } from "./context/NotificationContext";
import { ConfirmationProvider } from "./context/ConfirmationContext";
import ToastContainer from "./components/ToastContainer";
import ConfirmationDialog from "./components/ConfirmationDialog";
import RotaProtegida from "./components/RotaProtegida";
import Login from "./components/Login";
import Relatorio from "./components/Relatorio";
import Financiamento from "./components/Financiamento";
import Usuarios from "./components/Usuarios";
import Logs from "./components/Logs";
import Dashboard from "./components/Dashboard";
import Socios from "./components/Socios";
import API_URL from "./config";


// Tipos que usam o componente Relatorio genérico
const TIPOS_GENERICOS = ["mensalidade", "unidentis", "yelium", "contrib"];

function ThemeSwitcher() {
  const { tema, setTema } = useTema();
  const [aberto, setAberto] = useState(false);

  const temas = [
    { id: "clean", label: "Clean", cor: "#f9fafb", borda: "#d1d5db" },
    { id: "dark",  label: "Dark",  cor: "#1a1d2e", borda: "#323659" },
    { id: "blue",  label: "Blue",  cor: "#1d4ed8", borda: "#1e40af" },
  ];

  return (
    <div className="relative">
      <button
        onClick={() => setAberto(!aberto)}
        title="Alterar tema"
        className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 transition-colors"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="13.5" cy="6.5" r=".5" fill="currentColor" />
          <circle cx="17.5" cy="10.5" r=".5" fill="currentColor" />
          <circle cx="8.5" cy="7.5" r=".5" fill="currentColor" />
          <circle cx="6.5" cy="12.5" r=".5" fill="currentColor" />
          <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z" />
        </svg>
      </button>

      {aberto && (
        <div className="absolute right-0 top-full mt-1 bg-white border border-gray-100 rounded-xl shadow-lg py-2 px-3 z-50 flex items-center gap-2">
          {temas.map((t) => (
            <button
              key={t.id}
              onClick={() => { setTema(t.id); setAberto(false); }}
              title={t.label}
              className="flex flex-col items-center gap-1 group"
            >
              <span
                className={`w-6 h-6 rounded-full border-2 transition-all ${tema === t.id ? "scale-110" : "opacity-70 group-hover:opacity-100"}`}
                style={{ backgroundColor: t.cor, borderColor: tema === t.id ? "#3b82f6" : t.borda }}
              />
              <span className="text-gray-500 text-[10px]">{t.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ModalAlterarSenha({ onFechar }) {
  const { token } = useAuth();
  const [form, setForm] = useState({ senhaAtual: "", novaSenha: "", confirmar: "" });
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState("");
  const [ok, setOk] = useState(false);

  const handleSalvar = async () => {
    setErro("");
    if (!form.senhaAtual || !form.novaSenha || !form.confirmar) return setErro("Preencha todos os campos.");
    if (form.novaSenha !== form.confirmar) return setErro("A nova senha e a confirmação não coincidem.");
    if (form.novaSenha.length < 6) return setErro("A nova senha deve ter pelo menos 6 caracteres.");
    setSalvando(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/minha-senha`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ senhaAtual: form.senhaAtual, novaSenha: form.novaSenha }),
      });
      const msg = await res.json();
      if (!res.ok) return setErro(typeof msg === "string" ? msg : "Erro ao alterar senha.");
      setOk(true);
    } catch {
      setErro("Erro ao conectar ao servidor.");
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 px-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6 flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-800">Alterar Senha</h2>
          <button onClick={onFechar} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
        </div>

        {ok ? (
          <>
            <div className="px-4 py-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700 text-center">
              ✓ Senha alterada com sucesso!
            </div>
            <button onClick={onFechar} className="w-full h-10 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-colors">
              Fechar
            </button>
          </>
        ) : (
          <>
            <div className="flex flex-col gap-3">
              {[
                { label: "Senha atual", key: "senhaAtual" },
                { label: "Nova senha", key: "novaSenha" },
                { label: "Confirmar nova senha", key: "confirmar" },
              ].map(({ label, key }) => (
                <div key={key}>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{label}</label>
                  <input type="password" value={form[key]}
                    onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                    className="mt-1 w-full h-10 px-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition" />
                </div>
              ))}
              {erro && <p className="text-xs text-red-500">{erro}</p>}
            </div>
            <div className="flex gap-3 pt-1">
              <button onClick={onFechar} className="flex-1 h-10 border border-gray-200 text-gray-500 text-sm font-semibold rounded-lg hover:bg-gray-50 transition-colors">
                Cancelar
              </button>
              <button onClick={handleSalvar} disabled={salvando} className="flex-1 h-10 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-200 text-white text-sm font-semibold rounded-lg transition-colors">
                {salvando ? "Salvando..." : "Salvar"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Navbar({ tiposRelatorio }) {
  const { usuario, logout } = useAuth();
  const navigate = useNavigate();
  const [dropdownAberto, setDropdownAberto] = useState(false);
  const [modalSenha, setModalSenha] = useState(false);
  const podeVerRelatorios = ["admin", "financeiro", "comum"].includes(usuario?.perfil);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const temFinanciamento = tiposRelatorio.some((t) =>
    ["financiamento1", "financiamento2", "financiamento3", "financiamento4"].includes(t.rota)
  );

  const tiposExibidos = tiposRelatorio.filter((t) =>
    TIPOS_GENERICOS.includes(t.rota)
  );

  return (
    <nav className="w-full bg-white border-b border-gray-100 shadow-sm px-6 py-3">
      <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-1">
          <span className="text-sm font-bold text-blue-700 uppercase tracking-widest mr-4">SINDIÁGUA-PB</span>

          <NavLink to="/" end className={({ isActive }) =>
            `px-4 py-1.5 rounded-md text-sm font-semibold transition-colors ${isActive ? "bg-blue-50 text-blue-700" : "text-gray-500 hover:text-gray-800 hover:bg-gray-100"}`
          }>
            Início
          </NavLink>

          <NavLink to="/socios" className={({ isActive }) =>
            `px-4 py-1.5 rounded-md text-sm font-semibold transition-colors ${isActive ? "bg-blue-50 text-blue-700" : "text-gray-500 hover:text-gray-800 hover:bg-gray-100"}`
          }>
            Sócios
          </NavLink>

          {podeVerRelatorios && (
            <div className="relative">
              <button
                onClick={() => setDropdownAberto(!dropdownAberto)}
                className="px-4 py-1.5 rounded-md text-sm font-semibold transition-colors text-gray-500 hover:text-gray-800 hover:bg-gray-100 flex items-center gap-1"
              >
                Relatórios
                <span className={`text-xs transition-transform ${dropdownAberto ? "rotate-180" : ""}`}>▼</span>
              </button>

              {dropdownAberto && (
                <div className="absolute top-full left-0 mt-1 bg-white border border-gray-100 rounded-xl shadow-lg py-1 z-50 min-w-48">
                  {tiposExibidos.map((tipo) => (
                    <NavLink
                      key={tipo.rota}
                      to={`/${tipo.rota}`}
                      onClick={() => setDropdownAberto(false)}
                      className={({ isActive }) =>
                        `block px-4 py-2 text-sm font-medium transition-colors ${isActive ? "bg-blue-50 text-blue-700" : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"}`
                      }
                    >
                      {tipo.nome}
                    </NavLink>
                  ))}

                  {temFinanciamento && (
                    <NavLink
                      to="/financiamento"
                      onClick={() => setDropdownAberto(false)}
                      className={({ isActive }) =>
                        `block px-4 py-2 text-sm font-medium transition-colors ${isActive ? "bg-blue-50 text-blue-700" : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"}`
                      }
                    >
                      Financiamento
                    </NavLink>
                  )}
                </div>
              )}
            </div>
          )}

          {usuario?.perfil === "admin" && (
            <NavLink to="/usuarios" className={({ isActive }) =>
              `px-4 py-1.5 rounded-md text-sm font-semibold transition-colors ${isActive ? "bg-blue-50 text-blue-700" : "text-gray-500 hover:text-gray-800 hover:bg-gray-100"}`
            }>
              Usuários
            </NavLink>
          )}
          {usuario?.perfil === "admin" && (
            <NavLink to="/logs" className={({ isActive }) =>
              `px-4 py-1.5 rounded-md text-sm font-semibold transition-colors ${isActive ? "bg-blue-50 text-blue-700" : "text-gray-500 hover:text-gray-800 hover:bg-gray-100"}`
            }>
              Logs
            </NavLink>
          )}
        </div>

        <div className="flex items-center gap-3">
          <ThemeSwitcher />
          <button onClick={() => setModalSenha(true)}
            className="text-xs text-gray-400 hover:text-blue-600 hover:underline transition-colors cursor-pointer bg-transparent border-none p-0">
            {usuario?.nome} &middot; <span className="capitalize">{usuario?.perfil}</span>
          </button>
          <button onClick={handleLogout}
            className="px-3 py-1.5 text-xs font-semibold text-red-500 hover:bg-red-50 border border-red-200 rounded-lg transition-colors">
            Sair
          </button>
        </div>
      </div>
      {modalSenha && <ModalAlterarSenha onFechar={() => setModalSenha(false)} />}
    </nav>
  );
}


function SessionManager() {
  const { token, logout } = useAuth();
  const { aviso, segundosRestantes, resetar } = useSessionTimeout(logout, !!token);

  if (!aviso) return null;

  return (
    <SessionTimeoutModal
      segundosRestantes={segundosRestantes}
      onContinuar={resetar}
      onSair={logout}
    />
  );
}

function AppContent() {
  console.log("API_URL:", process.env.REACT_APP_API_URL);

  const { token, usuario } = useAuth();
  const [tiposRelatorio, setTiposRelatorio] = useState([]);
  const podeVerRelatorios = ["admin", "financeiro", "comum"].includes(usuario?.perfil);

  useEffect(() => {
    if (!token || !podeVerRelatorios) return;
    axios.get(`${API_URL}/api/relatorio/tipos`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => setTiposRelatorio(res.data))
      .catch(() => console.error("Erro ao carregar tipos de relatório."));
  }, [token]);

  const temFinanciamento = tiposRelatorio.some((t) =>
    ["financiamento1", "financiamento2", "financiamento3", "financiamento4"].includes(t.rota)
  );

  return (
    <BrowserRouter>
      <SessionManager />
      {usuario && <Navbar tiposRelatorio={tiposRelatorio} />}
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/" element={<RotaProtegida><Dashboard /></RotaProtegida>} />

        <Route path="/socios" element={
            <RotaProtegida perfis={["admin", "financeiro", "comum"]}>
                <Socios />
            </RotaProtegida>
        } />

        <Route path="/usuarios" element={
            <RotaProtegida perfis={["admin"]}>
                <Usuarios />
            </RotaProtegida>
        } />
        <Route path="/logs" element={
            <RotaProtegida perfis={["admin"]}>
                <Logs />
            </RotaProtegida>
        } />
        
        {/* Rotas genéricas geradas dinamicamente */}
        {tiposRelatorio
          .filter((t) => TIPOS_GENERICOS.includes(t.rota))
          .map((t) => (
            <Route key={t.rota} path={`/${t.rota}`} element={
              <RotaProtegida perfis={["admin", "financeiro", "comum"]}>
                <Relatorio tipo={t.rota} titulo={t.nome} codigo={t.codigo} />
              </RotaProtegida>
            } />
          ))}

        {/* Rota de financiamento */}
        {temFinanciamento && (
          <Route path="/financiamento" element={
            <RotaProtegida perfis={["admin", "financeiro", "comum"]}>
              <Financiamento />
            </RotaProtegida>
          } />
        )}
      </Routes>
    </BrowserRouter>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <NotificationProvider>
          <ConfirmationProvider>
            <AppContent />
            <ToastContainer />
            <ConfirmationDialog />
          </ConfirmationProvider>
        </NotificationProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}
